import FormFieldNew from "@/components/controls/Form/FormFieldNew";
import {
  FormCommonProps,
  FormFieldNewType
} from "@/components/controls/Form/types";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import React, { ReactNode } from "react";

export interface FormSectionProps extends FormCommonProps {
  id: string;
  title?: string;
  fields?: FormFieldNewType[];
  divider?: boolean;
  bottomCustomComponent?: ReactNode;
  mergeBottomComponent?: boolean;
  sectionClassName?: string;
  fieldsContainerClassName?: string;
  fieldsGapClassName?: string;
  halfFieldClassName?: string;
  titleActionButton?: ReactNode;
  dividerClassName?: string;
  allFields?: FormFieldNewType[];
  isLast?: boolean;
}

const FormSection: React.FC<FormSectionProps> = ({
  register,
  setValue,
  watch,
  title,
  fields = [],
  divider = false,
  bottomCustomComponent,
  mergeBottomComponent = false,
  sectionClassName,
  fieldsContainerClassName,
  fieldsGapClassName = "gap-2",
  halfFieldClassName,
  titleActionButton,
  dividerClassName,
  allFields,
  isLast = false
}) => {
  return (
    <div
      className={cn("w-full flex flex-col gap-4", sectionClassName)}
      data-test={`form-section-${title?.toLowerCase().replace(/\s+/g, "-") || "untitled"}`}
    >
      {title && (
        <div
          className={cn(
            "flex mt-2 justify-start",
            titleActionButton ? "justify-between" : "justify-start"
          )}
        >
          <Text variant="lgLabel">{title}</Text>
          {titleActionButton}
        </div>
      )}

      {(fields.length > 0 || (mergeBottomComponent && bottomCustomComponent)) && (
        <div className={cn("flex flex-col", fieldsGapClassName, fieldsContainerClassName)}>
          {(() => {
            const renderedFields: React.ReactNode[] = [];
            let i = 0;
            while (i < fields.length) {
              const field = fields[i];
              const nextField = fields[i + 1];
              const isCurrentHalf = field.layout === "half";
              const isNextHalf = nextField?.layout === "half";

              if (isCurrentHalf && isNextHalf) {
                // Group the two half-width fields in a row
                if (field.labelPosition === "left") {
                  // Shared-label layout: one label on the left, both inputs side by side in flex-1
                  renderedFields.push(
                    <div
                      key={`${field.name}-${nextField.name}`}
                      className="flex flex-row items-start gap-4 w-full"
                    >
                      <label
                        className={cn(
                          "flex shrink-0 items-center justify-between w-[200px] pt-2",
                          field.labelClassName
                        )}
                      >
                        <div className="flex flex-row items-center w-full gap-2">
                          <div className="flex flex-col">
                            <div className="flex flex-row">
                              <Text variant="bodySecondary">{field.label}</Text>
                              {(field.required ||
                                field.isDependentRequired) && (
                                <span className="text-primary ml-1">*</span>
                              )}
                            </div>
                            {field.subLabel && (
                              <Text variant="bodyTertiary">
                                {field.subLabel}
                              </Text>
                            )}
                          </div>
                        </div>
                      </label>
                      <div
                        className={cn(
                          "flex-1 flex items-start gap-2",
                          halfFieldClassName
                        )}
                      >
                        <div className="flex-1">
                          <FormFieldNew
                            field={{ ...field, suppressLabel: true }}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            allFields={allFields}
                          />
                        </div>
                        <div className="flex-1">
                          <FormFieldNew
                            field={{ ...nextField, suppressLabel: true }}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            allFields={allFields}
                          />
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  renderedFields.push(
                    <div
                      key={`${field.name}-${nextField.name}`}
                      className="flex items-center gap-2"
                    >
                      <div className="flex-1">
                        <FormFieldNew
                          field={field}
                          register={register}
                          setValue={setValue}
                          watch={watch}
                          allFields={allFields}
                        />
                      </div>
                      <div className={cn("flex-1", halfFieldClassName)}>
                        <FormFieldNew
                          field={nextField}
                          register={register}
                          setValue={setValue}
                          watch={watch}
                          allFields={allFields}
                        />
                      </div>
                    </div>
                  );
                }
                i += 2; // Skip the next field since we've processed it
              } else {
                // Render single field normally
                renderedFields.push(
                  <FormFieldNew
                    key={field.name}
                    field={field}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                    allFields={allFields}
                  />
                );
                i += 1;
              }
            }

            return renderedFields;
          })()}
          {mergeBottomComponent && bottomCustomComponent}
        </div>
      )}
      {!mergeBottomComponent && bottomCustomComponent}
      {divider && !isLast && (
        <div
          className={cn(
            "w-full border-t-[1px] border-border-border1",
            dividerClassName
          )}
        />
      )}
    </div>
  );
};

export default FormSection;
